import { useCallback, useMemo, useState } from 'react';
import { useTracking } from '@podiumhq/tracking';

import { validateEmail } from 'lib/fieldValidation';
import { validatePhone } from 'lib/phoneUtil';

import useCheckEmailExists from './useCheckEmailExists';
import useCheckPhoneExists from './useCheckPhoneExists';
import useCheckForInvite from './useCheckForInvite';

const useCheckAccountExists = (trackEventPath = 'trials') => {
  const checkEmailExists = useCheckEmailExists();
  const checkInviteExists = useCheckForInvite();
  const checkPhoneExists = useCheckPhoneExists();

  const { trackEvent } = useTracking();

  const [businessName, setBusinessName] = useState('');
  const [emailExists, setEmailExists] = useState(false);
  const [inviteExists, setInviteExists] = useState(false);
  const [phoneExists, setPhoneExists] = useState(false);

  const onError = useCallback(
    (error) => trackEvent(`${trackEventPath}.accountInfo.error`, { error }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const onSuccess = useCallback((accountExists, trackingParams = {}) => {
    if (accountExists) {
      trackEvent(`${trackEventPath}.accountExists.visited`, trackingParams);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkEmailAddressExists = useCallback(async (email) => {
    setEmailExists(false);

    if (email && validateEmail(email)) {
      try {
        const emailParams = { email };
        const emailExists = await checkEmailExists(emailParams);

        setEmailExists(emailExists);
        onSuccess(emailExists, emailParams);
        return emailExists;
      } catch (error) {
        onError(error?.message);
      }
    }

    return false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkPhoneNumberExists = useCallback(async (phone) => {
    setPhoneExists(false);

    if (phone && validatePhone(phone)) {
      try {
        const phoneParams = { phone };
        const phoneExists = await checkPhoneExists(phoneParams);

        setPhoneExists(phoneExists);
        onSuccess(phoneExists, phoneParams);
        return phoneExists;
      } catch (error) {
        onError(error?.message);
      }
    }

    return false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkForInvite = useCallback(
    async (email) => {
      setInviteExists(false);
      if (email && validateEmail(email)) {
        try {
          const emailParams = { email };
          const inviteExists = await checkInviteExists(emailParams);

          setInviteExists(inviteExists?.inviteExists);
          setBusinessName(inviteExists?.businessName);
          onSuccess(inviteExists, emailParams);

          return emailExists;
        } catch (error) {
          onError(error?.message);
        }
      }

      return false;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [emailExists]
  );

  return useMemo(
    () => ({
      accountExists: emailExists || phoneExists,
      businessName,
      checkEmailAddressExists,
      checkForInvite,
      checkPhoneNumberExists,
      emailExists,
      inviteExists,
      phoneExists,
    }),
    [
      businessName,
      checkEmailAddressExists,
      checkForInvite,
      checkPhoneNumberExists,
      emailExists,
      inviteExists,
      phoneExists,
    ]
  );
};

export default useCheckAccountExists;
